import type { SeoPage } from '@/lib/seo';
import { getLocale } from '@/paraglide/runtime.js';
import manifest from '@/content/control-resonant/page-manifest.json';
import chinese from '@/content/control-resonant/page-manifest.zh.json';
import sources from '@/content/control-resonant/source-registry.json';

export interface ContentPage extends SeoPage {
  h1: string;
  type: string;
  primaryKeyword: string;
  priority: string;
  canonical: string;
  updatedAt: string;
}
export interface GuideModule {
  default: React.ComponentType;
  quickAnswer: string;
  sourceIds?: string[];
  related?: string[];
  diagram?: {
    src: string;
    alt: string;
    caption?: string;
    sourceId?: string;
    width: number;
    height: number;
  };
  table?: { headers: string[]; rows: string[][] };
}
export const pages: ContentPage[] = manifest;
const modules = import.meta.glob<GuideModule>(
  '/src/content/control-resonant/*.mdx',
  { eager: true }
);
export function getPage(
  path: string,
  locale = getLocale()
): ContentPage | undefined {
  const page = pages.find((page) => page.url === path);
  if (!page) return undefined;
  const translation =
    locale === 'zh' ? chinese.find((item) => item.url === path) : undefined;
  if (locale === 'zh' && !translation)
    throw new Error(`Missing Chinese metadata: ${path}`);
  return {
    ...page,
    ...translation,
    locale,
  };
}
export function getContent(path: string, locale = getLocale()) {
  return modules[
    `/src/content/control-resonant/${path === '/' ? 'home' : path.slice(1).replaceAll('/', '__')}${locale === 'zh' ? '.zh' : ''}.mdx`
  ];
}
export function getChildren(path: string) {
  return pages
    .filter((page) => page.url.startsWith(path + '/'))
    .map((page) => getPage(page.url)!);
}
export function getSources(ids: string[]) {
  return ids
    .map((id) => sources.find((source) => source.id === id))
    .filter((source): source is (typeof sources)[number] => !!source);
}
export function relatedPages(page: ContentPage, explicit: string[] = []) {
  const parent = '/' + page.url.split('/')[1];
  const candidates = [
    ...explicit,
    ...getChildren(parent)
      .filter((other) => other.url !== page.url)
      .map((other) => other.url),
    '/guides/build-power',
    '/quests/power-lines',
  ];
  const selected = [...new Set(candidates)]
    .filter((path) => path !== page.url && path !== '/build-planner')
    .map((path) => getPage(path))
    .filter((item): item is ContentPage => !!item)
    .slice(0, 3);
  if (page.url !== '/build-planner') selected.push(getPage('/build-planner')!);
  return selected;
}
